import React, { useEffect, useState, useMemo } from "react";
import { Form, Col, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { mapUserRoles } from "../../../functions/useRoleMapping";
import { useSelector, useDispatch } from "react-redux";
import useHandleBlur from "../../../functions/useHandleBlur";
import useFieldClass from "../../../functions/useFieldClass";
// import { useFetchJobDetails } from "../../../functions/useFetchJobDetails"; // Reusable API hook
import { useFormState } from "../../../functions/useFormState"; // Reusable form hook
import { selectApiStatus } from "../../../redux/slices/apiStatusSlice";
import {
  selectUserDetails,
  selectUserRoles,
} from "../../../redux/slices/userSlice";
import { selectApiUrls } from "../../../redux/slices/apiUrlSlice";
import {
  selectPumpManifoldPiping,
  selectGeneralStation,
  selectFieldDescriptions,
  selectPumps,
} from "../../../redux/selectors";
import NoRecordFoundAlert from "../../common/NoRecordFoundAlert";
import MaterialMismatchWarning from "../../JobSchedulePage/MaterialMismatchWarning";
import { getMaterialMismatchData } from "../../JobSchedulePage/utils/materialMatchUtils";

const PumpManifoldPiping = (zone) => {
  const dispatch = useDispatch();
  const userDetails = useSelector(selectUserDetails); // User details
  const userRoles = useSelector(selectUserRoles); // User details
  const currentUser = userDetails?.name;
  const sectionName = "pumpManifoldPiping"; // Name of the component

  const apiUrls = useSelector(selectApiUrls); // Fetch API URLs from Redux
  const apiUrl = apiUrls[sectionName];
  const tableKey = sectionName;

  const apiStatus = useSelector(selectApiStatus); // Fetch API status from Redux
  const loading = apiStatus[sectionName] !== "success"; // Determine loading state based on API status

  const { options } = useSelector((state) => state.dropdown); // Fetch dropdown values from redux

  const storedJobDetails = useSelector(selectPumpManifoldPiping);
  const pumpParents = useSelector(selectPumps);

  const activetab = useSelector(
    (state) => state.activeMechanicalTab.selectedZone
  );

  const generalSpecsDetails = useSelector(selectGeneralStation);

  const stationColor =
    generalSpecsDetails.find((detail) => detail.Zone === activetab)?.Color ||
    "";
  const trackerType =
    generalSpecsDetails.find((detail) => detail.Zone === activetab)
      ?.TrackerType || "";

  const error = null;

  const jobDetails = useMemo(() => {
    const detail = storedJobDetails.find((detail) => detail.Zone === activetab);
    return detail || storedJobDetails[0] || {};
  }, [storedJobDetails, activetab]);

  // Get pump parent records for the active zone (contains MainShaftMaterial)
  const zonePumpParents = useMemo(() => {
    return pumpParents.filter((detail) => detail.Zone === activetab);
  }, [pumpParents, activetab]);

  const { formData, handleChange, setFormData } = useFormState({});

  // Calculate material mismatch - use formData for real-time validation
  const materialMismatch = useMemo(() => {
    // Use formData if available, otherwise fall back to jobDetails
    const currentData = {
      ColumnMaterialVT:
        formData.columnMaterialVT || jobDetails.ColumnMaterialVT,
    };
    const result = getMaterialMismatchData(currentData, zonePumpParents);
    console.log('PumpManifoldPiping - Material Mismatch Check:', {
      columnMaterial: currentData.ColumnMaterialVT,
      pumpParents: zonePumpParents,
      result,
    });
    return result;
  }, [formData.columnMaterialVT, jobDetails, zonePumpParents]);

  const fieldDescriptions = useSelector(selectFieldDescriptions);

  const getFieldTitle = (fieldName) => {
    return (
      fieldDescriptions.find((desc) => desc.ColumnName === fieldName)
        ?.Description || ""
    );
  };

  const renderTooltip = (fieldName) => (
    <Tooltip id={`tooltip-${fieldName}`}>
      {getFieldTitle(fieldName)
        .split("\n")
        .map((line, index) => (
          <span key={index}>
            {line}
            <br />
          </span>
        ))}
    </Tooltip>
  );

  useEffect(() => {
    if (jobDetails) {
      setFormData({
        ID: jobDetails.ID || "",
        pipingMaterial: jobDetails.PipingMaterial || "",
        pRVType: jobDetails.PRVType || "",
        hDPEAccessoryForSubPump: jobDetails.HDPEAccessoryForSubPump || "",
        subPumpDischargeHoseType: jobDetails.SubPumpDischargeHoseType || "",
        columnMaterialST: jobDetails.ColumnMaterialST || "",
        columnMaterialVT: jobDetails.ColumnMaterialVT || "",
        pumpManifoldPipingComments: jobDetails.PumpManifoldPipingComments || "",
        distanceFromBottomSub: jobDetails.DistanceFromBottomSub || "",
        distanceFromBottomVT: jobDetails.DistanceFromBottomVT || "",
        flowMeterType: jobDetails.FlowMeterType || "",
        injectionTaps: jobDetails.InjectionTaps || "",
        pipeCoating: jobDetails.PipeCoating || "",
        pipeColor: jobDetails.PipeColor || "",
        pRVSize: jobDetails.PRVSize || "",
        stationDischargeIsoValveOperator:
          jobDetails.StationDischargeIsoValveOperator || "",
        stationDischargeIsoValveSize:
          jobDetails.StationDischargeIsoValveSize || "",
        stationDischargeIsoValeStyle:
          jobDetails.StationDischargeIsoValeStyle || "",
        stationHardware: jobDetails.StationHardware || "",
        stationInletIsoValveOperator:
          jobDetails.StationInletIsoValveOperator || "",
        stationInletValveIsoSize: jobDetails.StationInletValveIsoSize || "",
        stationInletIsoValveStyle: jobDetails.StationInletIsoValveStyle || "",
        systemReliefPipedTo: jobDetails.SystemReliefPipedTo || "",
      });
    }
  }, [dispatch, jobDetails, setFormData]);

  const jobCheckoutStatus = useSelector((state) => state.checkout.job);
  const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
  const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
  const readOnly = useSelector((state) => state.readOnly.isReadOnly); // Fetch read-only state from Redux
  const isDisabled = (isJobCheckedOut && !isCurrentUserCheckedOut) || readOnly; // Update isDisabled condition

  // State to store role variables
  const [roles, setRoles] = useState({
    Admin: false,
    Apps: false,
    Engineer: false,
    TestPit: false,
    PSN: false,
    Sales: false,
    ReadOnly: false,
  });

  const { handleFocus, handleBlur, showNotification, saveStatus } =
    useHandleBlur(formData);

  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);

  // const { tbdFields } = useTbdCheck(formData, zone); // Pass zone to track TBD status for specific zone
  // const zoneTbdFields = zone ? tbdFields?.[zone] : tbdFields;

  const fieldClasses = useFieldClass(formData, zone); // Use useFieldClass hook

  /****************Add dropdown options here*************************/
  const pipingMaterialOptions =
    options?.filter((option) => option.DropdownType === "Piping Material").sort((a, b) => a.SortOrder - b.SortOrder) ||
    [];
  const stationDischargeIsoValveSizeOptions =
    options?.filter((option) => option.DropdownType === "Iso Valve Size").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const pipeCoatingOptions =
    options?.filter((option) => option.DropdownType === "Pipe Coating").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const stationDischargeIsoValeStyleOptions =
    options?.filter((option) => option.DropdownType === "Iso Valve Style").sort((a, b) => a.SortOrder - b.SortOrder) ||
    [];
  const stationDischargeIsoValveOperatorOptions =
    options?.filter((option) => option.DropdownType === "Iso Valve Operator").sort((a, b) => a.SortOrder - b.SortOrder) ||
    [];
  const pipeColorOptions =
    options?.filter((option) => option.DropdownType === "Pipe Color").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const flowMeterTypeOptions =
    options?.filter((option) => option.DropdownType === "Flow Meter Type").sort((a, b) => a.SortOrder - b.SortOrder) ||
    [];
  const pRVSizeOptions =
    options?.filter((option) => option.DropdownType === "PRV Size").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const pRVTypeOptions =
    options?.filter((option) => option.DropdownType === "PRV Type").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const columnMaterialVTOptions =
    options?.filter((option) => option.DropdownType === "Column Material").sort((a, b) => a.SortOrder - b.SortOrder) ||
    [];
  const columnMaterialSTOptions =
    options?.filter((option) => option.DropdownType === "Column Material").sort((a, b) => a.SortOrder - b.SortOrder) ||
    [];
  const subPumpDischargeHoseTypeOptions =
    options?.filter(
      (option) => option.DropdownType === "Sub Pump Discharge Hose Type"
    ).sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const hDPEAccessoryForSubPumpOptions =
    options?.filter(
      (option) => option.DropdownType === "HDPE Accessory for Sub Pump"
    ).sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const StationInletValveIsoSizeOptions =
    options?.filter((option) => option.DropdownType === "Iso Valve Size").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const stationInletIsoValveStyleOptions =
    options?.filter((option) => option.DropdownType === "Iso Valve Style").sort((a, b) => a.SortOrder - b.SortOrder) ||
    [];
  const stationInletIsoValveOperatorOptions =
    options?.filter((option) => option.DropdownType === "Iso Valve Operator").sort((a, b) => a.SortOrder - b.SortOrder) ||
    [];
  const stationHardwareOptions =
    options?.filter((option) => option.DropdownType === "Station Hardware").sort((a, b) => a.SortOrder - b.SortOrder) ||
    [];
  /***************************************************************** */

  const isValueInDropdown = (value, dropdownOptions) => {
    const exists = dropdownOptions.some(
      (option) => option.DisplayText === value || option.displayName === value
    );
    return exists;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    ); // Show loading spinner
  }

  if (error) {
    return <div className="error">{error}</div>; // Show error message if fetch fails
  }

  if (!loading && !jobDetails?.ID) {
    return (
      <NoRecordFoundAlert sectionName={sectionName} zone={zone} />
    );
  }

  return (
    <div>
      {error ? (
        <p style={{ color: "red" }}>Error: {error}</p>
      ) : !formData ? (
        <p>Loading...</p>
      ) : (
        <div>
          <div>
            {showNotification && (
              <div
                className={`save-notification ${
                  saveStatus.includes("Failed") ? "error" : ""
                }`}
              >
                {saveStatus}
              </div>
            )}
          </div>
          <div className="mechanical-responsive-grid">
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="pipingMaterial">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("PipingMaterial")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Piping Material</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="pipingMaterial"
                      value={formData.pipingMaterial || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(
                          e,
                          "Piping Material",
                          pipingMaterialOptions,
                          {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "PipingMaterial",
                            tableKey,
                          }
                        )
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.pipingMaterial}
                      onChange={handleChange}
                    >
                      <option value="">Select Piping Material</option>
                      {pipingMaterialOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(
                        formData.pipingMaterial,
                        pipingMaterialOptions
                      ) &&
                        formData.pipingMaterial && (
                          <option
                            value={formData.pipingMaterial}
                            style={{ color: "red" }}
                          >
                            *{formData.pipingMaterial}
                          </option>
                      )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>

              <Col>
                <Form.Group as={Row} controlId="stationDischargeIsoValveSize">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("StationDischargeIsoValveSize")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Station Discharge Iso Valve Size</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="stationDischargeIsoValveSize"
                      value={formData.stationDischargeIsoValveSize || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(
                          e,
                          "Station Discharge Iso Valve Size",
                          stationDischargeIsoValveSizeOptions,
                          {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "StationDischargeIsoValveSize",
                            tableKey,
                          }
                        )
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.stationDischargeIsoValveSize}
                      onChange={handleChange}
                    >
                      <option value="">
                        Select Station Discharge Iso Valve Size
                      </option>
                      {stationDischargeIsoValveSizeOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(
                        formData.stationDischargeIsoValveSize,
                        stationDischargeIsoValveSizeOptions
                      ) &&
                        formData.stationDischargeIsoValveSize && (
                          <option
                            value={formData.stationDischargeIsoValveSize}
                            style={{ color: "red" }}
                          >
                            *{formData.stationDischargeIsoValveSize}
                          </option>
                      )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="pipeCoating">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("PipeCoating")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Pipe Coating</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="pipeCoating"
                      value={formData.pipeCoating || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Pipe Coating", pipeCoatingOptions, {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "PipeCoating",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.pipeCoating}
                      onChange={handleChange}
                    >
                      <option value="">Select Pipe Coating</option>
                      {pipeCoatingOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(
                        formData.pipeCoating,
                        pipeCoatingOptions
                      ) &&
                        formData.pipeCoating && (
                          <option
                            value={formData.pipeCoating}
                            style={{ color: "red" }}
                          >
                            *{formData.pipeCoating}
                          </option>
                      )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>

              <Col>
                <Form.Group as={Row} controlId="stationDischargeIsoValeStyle">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("StationDischargeIsoValeStyle")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Station Discharge Iso Valve Style</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="stationDischargeIsoValeStyle"
                      value={formData.stationDischargeIsoValeStyle || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(
                          e,
                          "Station Discharge Iso Vale Style",
                          stationDischargeIsoValeStyleOptions,
                          {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "StationDischargeIsoValeStyle",
                            tableKey,
                          }
                        )
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.stationDischargeIsoValeStyle}
                      onChange={handleChange}
                    >
                      <option value="">
                        Select Station Discharge Iso Valve Style
                      </option>
                      {stationDischargeIsoValeStyleOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(
                        formData.stationDischargeIsoValeStyle,
                        stationDischargeIsoValeStyleOptions
                      ) &&
                        formData.stationDischargeIsoValeStyle && (
                          <option
                            value={formData.stationDischargeIsoValeStyle}
                            style={{ color: "red" }}
                          >
                            *{formData.stationDischargeIsoValeStyle}
                          </option>
                      )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="pipeColor">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("PipeColor")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Pipe Color</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="pipeColor"
                      value={formData.pipeColor || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Pipe Color", pipeColorOptions, {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "PipeColor",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.pipeColor}
                      onChange={handleChange}
                    >
                      <option value="">Select Pipe Color</option>
                      {pipeColorOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText === "Same as Station"
                            ? `Same as Station (${stationColor})`
                            : option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(
                        formData.pipeColor,
                        pipeColorOptions
                      ) &&
                        formData.pipeColor && (
                          <option
                            value={formData.pipeColor}
                            style={{ color: "red" }}
                          >
                            *{formData.pipeColor}
                          </option>
                      )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>

              <Col>
                <Form.Group
                  as={Row}
                  controlId="stationDischargeIsoValveOperator"
                >
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip(
                        "StationDischargeIsoValveOperator"
                      )}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Station Discharge Iso Valve Operator</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="stationDischargeIsoValveOperator"
                      value={formData.stationDischargeIsoValveOperator || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(
                          e,
                          "Station Discharge Iso Valve Operator",
                          stationDischargeIsoValveOperatorOptions,
                          {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "StationDischargeIsoValveOperator",
                            tableKey,
                          }
                        )
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.stationDischargeIsoValveOperator}
                      onChange={handleChange}
                    >
                      <option value="">
                        Select Station Discharge Iso Valve Operator
                      </option>
                      {stationDischargeIsoValveOperatorOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(
                        formData.stationDischargeIsoValveOperator,
                        stationDischargeIsoValveOperatorOptions
                      ) &&
                        formData.stationDischargeIsoValveOperator && (
                          <option
                            value={formData.stationDischargeIsoValveOperator}
                            style={{ color: "red" }}
                          >
                            *{formData.stationDischargeIsoValveOperator}
                          </option>
                      )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="pRVSize">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("PRVSize")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>PRV Size</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="pRVSize"
                      value={formData.pRVSize || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "PRV Size", pRVSizeOptions, {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "PRVSize",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.pRVSize}
                      onChange={handleChange}
                    >
                      <option value="">Select PRV Size</option>
                      {pRVSizeOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(
                        formData.pRVSize,
                        pRVSizeOptions
                      ) &&
                        formData.pRVSize && (
                          <option
                            value={formData.pRVSize}
                            style={{ color: "red" }}
                          >
                            *{formData.pRVSize}
                          </option>
                      )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>

              <Col>
                <Form.Group as={Row} controlId="flowMeterType">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("FlowMeterType")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Flow Meter Type</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="flowMeterType"
                      value={formData.flowMeterType || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Flow Meter Type", flowMeterTypeOptions, {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "FlowMeterType",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.flowMeterType}
                      onChange={handleChange}
                    >
                      <option value="">Select Flow Meter Type</option>
                      {flowMeterTypeOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(
                        formData.flowMeterType,
                        flowMeterTypeOptions
                      ) &&
                        formData.flowMeterType && (
                          <option
                            value={formData.flowMeterType}
                            style={{ color: "red" }}
                          >
                            *{formData.flowMeterType}
                          </option>
                      )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="injectionTaps">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("InjectionTaps")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Injection Taps</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="injectionTaps"
                      value={formData.injectionTaps || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Injection Taps", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "InjectionTaps",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.injectionTaps}
                      onChange={handleChange}
                      placeholder="Enter Injection Taps"
                    />
                  </Col>
                </Form.Group>
              </Col>

              <Col>
                <Form.Group as={Row} controlId="pRVType">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("PRVType")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>PRV Type</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="pRVType"
                      value={formData.pRVType || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "PRV Type", pRVTypeOptions, {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "PRVType",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.pRVType}
                      onChange={handleChange}
                    >
                      <option value="">Select PRV Type</option>
                      {pRVTypeOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(
                        formData.pRVType,
                        pRVTypeOptions
                      ) &&
                        formData.pRVType && (
                          <option
                            value={formData.pRVType}
                            style={{ color: "red" }}
                          >
                            *{formData.pRVType}
                          </option>
                      )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            {trackerType === "VT" && (
              <>
                <Row className="mb-3">
                  <Col>
                    <Form.Group as={Row} controlId="columnMaterialVT">
                      <Form.Label column md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("ColumnMaterialVT")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Column Material VT</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col>
                        <Form.Select
                          name="columnMaterialVT"
                          value={formData.columnMaterialVT || ""}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(
                              e,
                              "Column Material VT",
                              columnMaterialVTOptions,
                              {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "ColumnMaterialVT",
                                tableKey,
                              }
                            )
                          }
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.columnMaterialVT}
                          onChange={handleChange}
                        >
                          <option value="">Select Column Material VT</option>
                          {columnMaterialVTOptions.map((option) => (
                            <option key={option.ID} value={option.DisplayText}>
                              {option.DisplayText}
                            </option>
                          ))}
                          {!isValueInDropdown(
                            formData.columnMaterialVT,
                            columnMaterialVTOptions
                          ) &&
                            formData.columnMaterialVT && (
                              <option
                                value={formData.columnMaterialVT}
                                style={{ color: "red" }}
                              >
                                *{formData.columnMaterialVT}
                              </option>
                          )}
                        </Form.Select>
                      </Col>
                      <Col md={12}>
                        <MaterialMismatchWarning 
                          mismatchData={materialMismatch} 
                          fieldType="column" 
                        />
                      </Col>
                    </Form.Group>
                  </Col>

                  <Col>
                    <Form.Group as={Row} controlId="columnMaterialST">
                      <Form.Label column md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("ColumnMaterialST")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Column Material ST</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col>
                        <Form.Select
                          name="columnMaterialST"
                          value={formData.columnMaterialST || ""}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(
                              e,
                              "Column Material ST",
                              columnMaterialSTOptions,
                              {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "ColumnMaterialST",
                                tableKey,
                              }
                            )
                          }
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.columnMaterialST}
                          onChange={handleChange}
                        >
                          <option value="">Select Column Material ST</option>
                          {columnMaterialSTOptions.map((option) => (
                            <option key={option.ID} value={option.DisplayText}>
                              {option.DisplayText}
                            </option>
                          ))}
                          {!isValueInDropdown(
                            formData.columnMaterialST,
                            columnMaterialSTOptions
                          ) &&
                            formData.columnMaterialST && (
                              <option
                                value={formData.columnMaterialST}
                                style={{ color: "red" }}
                              >
                                *{formData.columnMaterialST}
                              </option>
                          )}
                        </Form.Select>
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <Form.Group as={Row} controlId="distanceFromBottomVT">
                      <Form.Label column md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("DistanceFromBottomVT")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Distance From Bottom VT</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col>
                        <Form.Control
                          type="text"
                          name="distanceFromBottomVT"
                          value={formData.distanceFromBottomVT || ""}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Distance From Bottom VT", [], {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "DistanceFromBottomVT",
                              tableKey,
                            })
                          }
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.distanceFromBottomVT}
                          onChange={handleChange}
                          placeholder="Enter Distance From Bottom VT"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group as={Row} controlId="distanceFromBottomSub">
                      <Form.Label column md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("DistanceFromBottomSub")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Distance From Bottom Sub</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col>
                        <Form.Control
                          type="text"
                          name="distanceFromBottomSub"
                          value={formData.distanceFromBottomSub || ""}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Distance From Bottom Sub", [], {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "DistanceFromBottomSub",
                              tableKey,
                            })
                          }
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.distanceFromBottomSub}
                          onChange={handleChange}
                          placeholder="Enter Distance From Bottom Sub"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <Form.Group as={Row} controlId="hDPEAccessoryForSubPump">
                      <Form.Label column md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("HDPEAccessoryForSubPump")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>HDPE Accessory For Sub Pump</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col>
                        <Form.Select
                          name="hDPEAccessoryForSubPump"
                          value={formData.hDPEAccessoryForSubPump || ""}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(
                              e,
                              "HDPE Accessory For Sub Pump",
                              hDPEAccessoryForSubPumpOptions,
                              {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "HDPEAccessoryForSubPump",
                                tableKey,
                              }
                            )
                          }
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.hDPEAccessoryForSubPump}
                          onChange={handleChange}
                        >
                          <option value="">
                            Select HDPE Accessory For Sub Pump
                          </option>
                          {hDPEAccessoryForSubPumpOptions.map((option) => (
                            <option key={option.ID} value={option.DisplayText}>
                              {option.DisplayText}
                            </option>
                          ))}
                          {!isValueInDropdown(
                            formData.hDPEAccessoryForSubPump,
                            hDPEAccessoryForSubPumpOptions
                          ) &&
                            formData.hDPEAccessoryForSubPump && (
                              <option
                                value={formData.hDPEAccessoryForSubPump}
                                style={{ color: "red" }}
                              >
                                *{formData.hDPEAccessoryForSubPump}
                              </option>
                          )}
                        </Form.Select>
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group as={Row} controlId="subPumpDischargeHoseType">
                      <Form.Label column md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("SubPumpDischargeHoseType")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Sub Pump Discharge Hose Type</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col>
                        <Form.Select
                          name="subPumpDischargeHoseType"
                          value={formData.subPumpDischargeHoseType || ""}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(
                              e,
                              "Sub Pump Discharge Hose Type",
                              subPumpDischargeHoseTypeOptions,
                              {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "SubPumpDischargeHoseType",
                                tableKey,
                              }
                            )
                          }
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.subPumpDischargeHoseType}
                          onChange={handleChange}
                        >
                          <option value="">
                            Select Sub Pump Discharge Hose Type
                          </option>
                          {subPumpDischargeHoseTypeOptions.map((option) => (
                            <option key={option.ID} value={option.DisplayText}>
                              {option.DisplayText}
                            </option>
                          ))}
                          {!isValueInDropdown(
                            formData.subPumpDischargeHoseType,
                            subPumpDischargeHoseTypeOptions
                          ) &&
                            formData.subPumpDischargeHoseType && (
                              <option
                                value={formData.subPumpDischargeHoseType}
                                style={{ color: "red" }}
                              >
                                *{formData.subPumpDischargeHoseType}
                              </option>
                          )}
                        </Form.Select>
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}

            {trackerType === "HC" && (
              <>
                <Row className="mb-3">
                  <Col>
                    <Form.Group as={Row} controlId="stationInletValveIsoSize">
                      <Form.Label column md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("StationInletValveIsoSize")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Station Inlet Iso Valve Size</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col>
                        <Form.Select
                          name="stationInletValveIsoSize"
                          value={formData.stationInletValveIsoSize || ""}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(
                              e,
                              "Station Inlet Iso Valve Size",
                              StationInletValveIsoSizeOptions,
                              {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "StationInletValveIsoSize",
                                tableKey,
                              }
                            )
                          }
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.StationInletValveIsoSize}
                          onChange={handleChange}
                        >
                          <option value="">
                            Select Station Inlet Iso Valve Size
                          </option>
                          {StationInletValveIsoSizeOptions.map((option) => (
                            <option key={option.ID} value={option.DisplayText}>
                              {option.DisplayText}
                            </option>
                          ))}
                          {!isValueInDropdown(
                            formData.stationInletValveIsoSize,
                            StationInletValveIsoSizeOptions
                          ) &&
                            formData.stationInletValveIsoSize && (
                              <option
                                value={formData.stationInletValveIsoSize}
                                style={{ color: "red" }}
                              >
                                *{formData.stationInletValveIsoSize}
                              </option>
                          )}
                        </Form.Select>
                      </Col>
                    </Form.Group>
                  </Col>

                  <Col>
                    <Form.Group as={Row} controlId="systemReliefPipedTo">
                      <Form.Label column md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("SystemReliefPipedTo")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>System Relief Piped To</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col>
                        <Form.Control
                          type="text"
                          name="systemReliefPipedTo"
                          value={formData.systemReliefPipedTo || ""}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "System Relief Piped To", [], {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "SystemReliefPipedTo",
                              tableKey,
                            })
                          }
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.systemReliefPipedTo}
                          onChange={handleChange}
                          placeholder="Enter System Relief Piped To"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col>
                    <Form.Group as={Row} controlId="stationInletIsoValveOperator">
                      <Form.Label column md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("StationInletIsoValveOperator")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Station Inlet Iso Valve Operator</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col>
                        <Form.Select
                          name="stationInletIsoValveOperator"
                          value={formData.stationInletIsoValveOperator || ""}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(
                              e,
                              "Station Inlet Iso Valve Operator",
                              stationInletIsoValveOperatorOptions,
                              {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "StationInletIsoValveOperator",
                                tableKey,
                              }
                            )
                          }
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.stationInletIsoValveOperator}
                          onChange={handleChange}
                        >
                          <option value="">
                            Select Station Inlet Iso Valve Operator
                          </option>
                          {stationInletIsoValveOperatorOptions.map((option) => (
                            <option key={option.ID} value={option.DisplayText}>
                              {option.DisplayText}
                            </option>
                          ))}
                          {!isValueInDropdown(
                            formData.stationInletIsoValveOperator,
                            stationInletIsoValveOperatorOptions
                          ) &&
                            formData.stationInletValveIsoOperator && (
                              <option
                                value={formData.stationInletIsoValveOperator}
                                style={{ color: "red" }}
                              >
                                *{formData.stationInletIsoValveOperator}
                              </option>
                          )}
                        </Form.Select>
                      </Col>
                    </Form.Group>
                  </Col>

                  <Col>
                    <Form.Group as={Row} controlId="stationInletIsoValveStyle">
                      <Form.Label column md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("StationInletValveIsoValveStyle")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Station Inlet Iso Valve Style</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col>
                        <Form.Select
                          name="stationInletIsoValveStyle"
                          value={formData.stationInletIsoValveStyle || ""}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(
                              e,
                              "Station Inlet Iso Valve Style",
                              stationInletIsoValveStyleOptions,
                              {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "StationInletIsoValveStyle",
                                tableKey,
                              }
                            )
                          }
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.stationInletIsoValveStyle}
                          onChange={handleChange}
                        >
                          <option value="">
                            Select Station Inlet Iso Valve Style
                          </option>
                          {stationInletIsoValveStyleOptions.map((option) => (
                            <option key={option.ID} value={option.DisplayText}>
                              {option.DisplayText}
                            </option>
                          ))}
                          {!isValueInDropdown(
                            formData.stationInletIsoValveStyle,
                            stationInletIsoValveStyleOptions
                          ) &&
                            formData.stationInletValveIsoStyle && (
                              <option
                                value={formData.stationInletIsoValveStyle}
                                style={{ color: "red" }}
                              >
                                *{formData.stationInletIsoValveStyle}
                              </option>
                          )}
                        </Form.Select>
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="stationHardware">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("StationHardware")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Station Hardware</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="stationHardware"
                      value={formData.stationHardware || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(
                          e,
                          "Station Hardware",
                          stationHardwareOptions,
                          {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "StationHardware",
                            tableKey,
                          }
                        )
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.stationHardware}
                      onChange={handleChange}
                    >
                      <option value="">Select Station Hardware</option>
                      {stationHardwareOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(
                        formData.stationHardware,
                        stationHardwareOptions
                      ) &&
                        formData.stationHardware && (
                          <option
                            value={formData.stationHardware}
                            style={{ color: "red" }}
                          >
                            *{formData.stationHardware}
                          </option>
                      )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>

              <Col></Col>
            </Row>
            <Row className="mb-3 mechanical-form-full-width">
              <Col>
                <Form.Group controlId="pumpManifoldPipingComments">
                  <Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("PumpManifoldPipingComments")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Comments</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="pumpManifoldPipingComments"
                    value={formData.pumpManifoldPipingComments || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Pump Manifold Piping Comments", [], {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "PumpManifoldPipingComments",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.pumpManifoldPipingComments}
                    onChange={handleChange}
                    placeholder="Enter Comments"
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>
        </div>
      )}
    </div>
  );
};

export default PumpManifoldPiping;
